import React, { FormEvent, useState } from 'react';
import { ScrollAnimate } from '../components/ScrollAnimate';
import { CountryCombobox } from '../components/CountryCombobox';
import { SEO } from '../components/SEO';
import { COUNTRIES } from '../data/countries';
import { IconEnvelope, IconHeadset, IconShield } from '../components/icons/SiteIcons';

const interestOptions = [
  'ShieldSME SaaS',
  'Managed Detection & Response',
  'Full Managed Security',
  'Enterprise Security',
  'Custom Engagement',
  'General Inquiry',
];

export function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [country, setCountry] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!country.trim() || !COUNTRIES.includes(country)) return;

    const endpoint = process.env.REACT_APP_FORMSPREE_ENDPOINT?.trim();
    if (!endpoint) {
      setSubmitError(
        'Something went wrong. Please email us directly at hello@lognsec.com'
      );
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const formData = new FormData(form);
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        setSubmitError(
          'Something went wrong. Please email us directly at hello@lognsec.com'
        );
        return;
      }
      form.reset();
      setCountry('');
      setSubmitted(true);
    } catch {
      setSubmitError(
        'Something went wrong. Please email us directly at hello@lognsec.com'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact logNsec"
        description="Talk to our security team. We respond within 24 hours."
        canonical="/contact"
      />
      <section className="bg-[#F5F5F5] px-5 py-12 md:px-8 md:py-16">
        <div className="mx-auto max-w-3xl text-center md:text-left">
          <ScrollAnimate>
            <h1 className="text-section">Let&apos;s Talk Security</h1>
            <p className="mt-4 text-body-site">
              Whether ShieldSME, managed services, or a custom engagement — we want to hear
              from you.
            </p>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-white px-5 py-20 md:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-12">
          <ScrollAnimate>
            <div>
              <h2 className="text-2xl font-bold text-[#1C1E21]">Get in Touch</h2>
              <div className="mt-8 space-y-4">
                {[
                  {
                    title: 'General Inquiries',
                    icon: <IconEnvelope size={24} />,
                    email: 'hello@lognsec.com',
                  },
                  {
                    title: 'Security Services',
                    icon: <IconShield size={24} />,
                    email: 'security@lognsec.com',
                  },
                  {
                    title: 'ShieldSME Support',
                    icon: <IconHeadset size={24} />,
                    email: 'support@lognsec.com',
                  },
                ].map((c) => (
                  <article key={c.title} className="card-site flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#E7F0FF]">
                      {c.icon}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#65676B]">{c.title}</p>
                      <a
                        href={`mailto:${c.email}`}
                        className="mt-1 inline-block text-[17px] font-medium text-[#0064E0] hover:underline"
                      >
                        {c.email}
                      </a>
                    </div>
                  </article>
                ))}
              </div>
              <div className="mt-10">
                <p className="text-label-site">What to Expect</p>
                <ol className="mt-4 space-y-3">
                  {[
                    'Send us a message',
                    'We respond within 24 hours',
                    'Free 30-minute consultation call',
                    'Tailored solution proposal',
                  ].map((step, i) => (
                    <li key={step} className="flex gap-3 text-[17px] text-[#1C1E21]">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E7F0FF] text-sm font-bold text-[#0064E0]">
                        {i + 1}
                      </span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </ScrollAnimate>

          <ScrollAnimate>
            {submitted ? (
              <div className="rounded-2xl border border-[#E4E6EB] bg-white p-10 text-center shadow-[var(--shadow-sm)]">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#E7F0FF] text-[#0064E0]">
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="mt-6 text-2xl font-bold text-[#0064E0]">Message Sent!</p>
                <p className="mt-3 text-body-site">
                  Thank you. We will be in touch within 24 hours.
                </p>
                <a
                  href="mailto:hello@lognsec.com"
                  className="mt-6 inline-block text-lg font-semibold text-[#0064E0] hover:underline"
                >
                  hello@lognsec.com
                </a>
                <div className="mt-8">
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setSubmitError(null);
                    }}
                    className="text-sm font-semibold text-[#65676B] hover:text-[#0064E0]"
                  >
                    Send another message
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="rounded-2xl border border-[#E4E6EB] bg-white p-8 shadow-[var(--shadow-sm)]"
              >
                <div className="space-y-4">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-semibold text-[#1C1E21]">
                      Full Name
                    </label>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      required
                      className="mt-2 w-full rounded-lg border border-[#E4E6EB] px-4 py-3 text-[17px] text-[#1C1E21] outline-none focus:border-[#0064E0]"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-[#1C1E21]">
                      Business Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="mt-2 w-full rounded-lg border border-[#E4E6EB] px-4 py-3 text-[17px] text-[#1C1E21] outline-none focus:border-[#0064E0]"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-[#1C1E21]">
                      Company Name
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      required
                      className="mt-2 w-full rounded-lg border border-[#E4E6EB] px-4 py-3 text-[17px] text-[#1C1E21] outline-none focus:border-[#0064E0]"
                    />
                  </div>
                  <div>
                    <label htmlFor="country" className="block text-sm font-semibold text-[#1C1E21]">
                      Country
                    </label>
                    <div className="mt-2">
                      <CountryCombobox
                        id="country"
                        name="country"
                        value={country}
                        onChange={setCountry}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="interest" className="block text-sm font-semibold text-[#1C1E21]">
                      I am interested in
                    </label>
                    <select
                      id="interest"
                      name="interest"
                      required
                      className="mt-2 w-full rounded-lg border border-[#E4E6EB] px-4 py-3 text-[17px] text-[#1C1E21] outline-none focus:border-[#0064E0]"
                    >
                      <option value="">Select an option</option>
                      {interestOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-[#1C1E21]">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      className="mt-2 min-h-[120px] w-full resize-y rounded-lg border border-[#E4E6EB] px-4 py-3 text-[17px] text-[#1C1E21] outline-none focus:border-[#0064E0]"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-6 w-full rounded-lg bg-[#0064E0] py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#0056C8]"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                {submitError ? (
                  <p className="mt-3 text-[15px] leading-relaxed text-red-600">
                    {submitError}
                  </p>
                ) : null}
              </form>
            )}
          </ScrollAnimate>
        </div>
      </section>
    </>
  );
}
